import React, { useContext, useState } from 'react'
import { Alert, Container, Form } from 'react-bootstrap'
import TagManager from 'react-gtm-module'
import { useTranslation } from 'react-i18next'
import { BoxInfo } from 'exports'
import { checkIsMobile, getCustomerInfoCookieName } from 'utils/helpers'
import { ISupport } from '../../interfaces/i-api-support'
import useStateWithSessionStorage from '../../hooks/useStateWithSessionStorage'
import { ICustomer } from '../../interfaces/i-api-loan'
import supportService from '../../services/supportService'
import { ValidationSupport } from '../../utils/validations/validationDecorators'
import useForm from '../../hooks/useForm'
import appConfig from 'app.config'
import MyPagesContext from 'contexts/MyPagesContext'

function SupportPage() {
	const { t } = useTranslation()
	const { staticCountryValues, productType } = useContext(MyPagesContext)
	const isMobile = checkIsMobile()
	const [hideContactForm, setHideContactForm] = useState(false)

	const [value] = useStateWithSessionStorage(getCustomerInfoCookieName())
	const [personal] = useState<ICustomer>(JSON.parse(value.toString()))

	const [values, setValues] = useState<ISupport>({
		data: {
			subject: '',
			emailFrom: personal.contacts.email,
			body: '',
			countryCode: appConfig.country,
		},
	})

	const { handleChange, handleSubmit, errors }: any = useForm(
		submit,
		values,
		setValidator,
	)

	const tagManagerArgs = {
		dataLayer: {
			userId: '001',
			userProject: 'project',
			page: '/support',
		}
	}

	React.useEffect(() => {
		TagManager.dataLayer(tagManagerArgs)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	function setValidator(obj: any) {
		return new ValidationSupport(obj)
	}

	const ResponseMessage = (isSuccess: boolean) => {
		return (
			<div className="mt-5 mb-5 response">
				{isSuccess ? (
					<>
						<BoxInfo css={'success'} message={t('ThankYou')} />
						<p>{t('ThankYouText')}</p>
					</>
				) : (
					<>
						<Alert variant="danger">{t('EmailNotSent')}</Alert>
					</>
				)}
			</div>
		)
	}

	const [responseMessage, setResponseMessage] = useState(<></>)
	const [disabled, setDisabled] = useState(false)

	async function submit(values: ISupport) {
		try {
			await supportService.sendEmail(values.data)
			setResponseMessage(ResponseMessage(true))
			setHideContactForm(true)
			setValues({
				...values,
				data: {
					subject: '',
					emailFrom: personal.contacts.email,
					body: '',
					countryCode: appConfig.country,
				},
			})
			setDisabled(true)
		} catch (error) {
			setResponseMessage(ResponseMessage(false))
		}
	}

	return (
		<div id="support-page">
			<Container className="px-0 bg">
				<Container
					id={
						isMobile
							? 'mobile-support-container'
							: 'desktop-support-container'
					}>
					<div
						className={
							isMobile ? 'text-div' : 'text-div float-left'
						}>
						<h3 className="top-title">{t('ContactUs')}</h3>
						<p>{t('ContactText')}</p>
						{staticCountryValues?.productSpecific.map(
							(value, key) => {
								if (value.productType === productType) {
									return (
										<div key={key} className= {appConfig.country === 'lv' ? 'department-delayed-payments' : ''}>
											{appConfig.country === 'lv' ? (
												<p>
													<b>
														{t(
															'CustomerSupport',
														)}
													</b>
												</p>
											) : (
												<></>
											)}
											<p>
												<b>{t('Phone')}: </b>
												{value.contactInformation
													.phoneNumber2 !==
													undefined &&
												appConfig.country === 'fi' ? (
													<div className="department-delayed-payments">
														{value
															.contactInformation
															.phoneNumber +
															' ' +
															`(${t(
																'RegularDepositAccount',
															)}) `}
														{value
															.contactInformation
															.phoneNumber2 +
															' ' +
															`(${t(
																'RegularLoan',
															)})`}
													</div>
												) : (
													<>
														{
															value
																.contactInformation
																.phoneNumber
														}
													</>
												)}
											</p>
											<p>
												<b>{t('Email')}: </b>
												{value.contactInformation.email}
											</p>
											{value.contactInformation.email2 !==
												undefined &&
											appConfig.country === 'lv' ? (
												<div className="department-delayed-payments">
													<p>
														<b>
															{t(
																'DepartmentOfDelayedPayments',
															)}
														</b>
													</p>
													<p>
														<b>{t('Phone')}: </b>
														{
															value
																.contactInformation
																.phoneNumber2
														}
													</p>
													<p>
														<b>{t('Email')}: </b>
														{
															value
																.contactInformation
																.email2
														}
													</p>
												</div>
											) : (
												<></>
											)}
										</div>
									)
								} else return null
							},
						)}
						{responseMessage}
					</div>
					{!hideContactForm && (
						<div
							className={
								isMobile ? 'form-div' : 'form-div float-right'
							}>
							<h4>{t('ContactForm')}</h4>
							<Form onSubmit={handleSubmit} noValidate>
								<div className="d-inline input-full float-left">
									<label className="label-input"
									       htmlFor='subject-input'>
										{t('Subject')}
									</label>
									<input
									    id='subject-input'
										name={'data.subject'}
										onChange={handleChange}
										className={
											errors.subject
												? 'input-form input-error'
												: 'input-form'
										}
										type="text"
										defaultValue={values.data.subject}
										disabled={disabled}
									/>
									{errors && (
										<label className="error-message">
											{errors.subject}
										</label>
									)}
								</div>
								<div className="d-inline input-full float-left">
									<label className="label-input"
									        htmlFor='body-input'>
										{t('Message')}
									</label>
									<textarea
									    id='body-input'
										name={'data.body'}
										onChange={handleChange}
										style={{
											maxHeight: '175px',
											height: '175px',
										}}
										className={
											errors.body
												? 'input-form input-error'
												: 'input-form'
										}
										defaultValue={values.data.body}
										disabled={disabled}></textarea>
									{errors && (
										<label className="error-message">
											{errors.body}
										</label>
									)}
								</div>
								<button
									className="btn btn-secondary mt-3"
									type="submit"
									disabled={disabled}>
									{t('Send')}
								</button>
							</Form>
						</div>
					)}
				</Container>
			</Container>
		</div>
	)
}
export default SupportPage
